import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "@/config/axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIActorOperationLogs() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [actorId]);

  const fetchLogs = async () => {
    try {
      setLoadingLogs(true);
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        `${API}/admin/ai-email/logs/${actorId}?limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch logs", error);
    } finally {
      setLoadingLogs(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/ai/actors/manage/${actorId}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Manage
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Operation Logs</h1>
            <p className="text-sm text-muted-foreground">
              History of all actions performed by the AI assistant
            </p>
          </div>
        </div>

        {/* Operation Logs Card */}
        <Card>
          <CardHeader>
            <CardTitle>AI Operation Logs</CardTitle>
            <CardDescription>
              History of all actions performed by the AI assistant
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingLogs ? (
              <p className="text-sm text-muted-foreground">Loading logs...</p>
            ) : (logs || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No operations logged yet</p>
            ) : (
              <div className="space-y-2">
                {logs.map((log) => (
                  <div key={log.id} className="border rounded p-3 flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-1 ${log.result === 'success' ? 'bg-green-500' : 'bg-red-500'}`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{log.description}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <Badge variant="outline" className="text-xs">{log.action_type}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                        {(log.affected_emails || []).length > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {(log.affected_emails || []).length} email(s) affected
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
